import { FC } from "preact/compat";
import { useEffect } from "preact/hooks";
import { useSignal } from "@preact/signals";
import { BASE_TAGS } from "~/core/slices/help/tags";
import { SuggestionsProps } from "./interfaces";
import LabelIcon from "@material-design-icons/svg/outlined/label.svg";
import OpenInNewIcon from "@material-design-icons/svg/outlined/open_in_new.svg";

const PAGES = [
    "dashboard",
    "admin/time?calendar=month",
    "admin/time?calendar=week",
    "admin/time?calendar=day",
    "profile",
];

const Suggestions: FC<SuggestionsProps> = ({ suggest, onSelect }) => {
    const visible = useSignal(true);

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (!suggest.peek().active || !visible.peek()) {
                return;
            }

            if (e.key === "Escape") {
                visible.value = false;
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                document
                    .querySelector<HTMLElement>("[data-content-editor]")
                    ?.focus();

                return;
            }

            const container = document.querySelector<HTMLElement>(
                "[data-suggest-container]",
            );

            if (!container) {
                return;
            }

            if (!document.activeElement?.hasAttribute("data-suggest")) {
                if (
                    container.firstElementChild instanceof HTMLElement &&
                    e.key === "ArrowDown"
                ) {
                    container.firstElementChild.focus();
                    e.preventDefault();
                }

                return;
            }

            if (e.key !== "ArrowDown" && e.key !== "ArrowUp") {
                if (e.key !== "Enter") {
                    document
                        .querySelector<HTMLElement>("[data-content-editor]")
                        ?.focus();
                }

                return;
            }

            const current = document.activeElement as HTMLElement;
            const index = Array.from(container.children).indexOf(current);
            const shift = e.key === "ArrowDown" ? 1 : -1;
            const nextIndex =
                index + shift < 0
                    ? container.children.length - 1
                    : (index + shift) % container.children.length;

            (container.children[nextIndex] as HTMLElement)?.focus();
            (container.children[nextIndex] as HTMLElement)?.scrollIntoView({
                block: "nearest",
            });
            e.preventDefault();
        };

        window.addEventListener("keydown", onKeyDown, true);

        return () => {
            window.removeEventListener("keydown", onKeyDown, true);
        };
    }, [suggest, visible]);

    useEffect(() => {
        visible.value = true;
    }, [visible, suggest.value.openIndex]);

    const finalVisible = visible.value && suggest.value.active;

    if (!finalVisible) {
        return null;
    }

    const tags = BASE_TAGS.filter((tag) =>
        tag.tag.startsWith(suggest.value.content),
    );
    const pages = PAGES.filter((page) =>
        page.startsWith(suggest.value.content),
    );
    const suggestions = tags
        .map((tag) => ({ name: tag.tag as string, t: "tag" }))
        .concat(pages.map((page) => ({ name: page, t: "page" })))
        .slice(0, 14);

    if (
        suggestions.length === 1 &&
        suggestions[0].name === suggest.value.content
    ) {
        return null;
    }

    return (
        <div
            style={{
                top: suggest.value.top,
                left: suggest.value.left,
            }}
            className="fixed z-20 mb-2 max-h-[300px] min-w-[120px] -translate-y-full overflow-y-auto border border-gray-200 bg-white text-sm shadow-lg"
        >
            {suggestions.length > 0 ? (
                <div
                    data-suggest-container={tags.length}
                    className="flex flex-col"
                >
                    {suggestions.map((tag) => (
                        <button
                            key={tag.name}
                            type="button"
                            data-suggest={tag.name}
                            onClick={() => {
                                onSelect(tag.name);
                                visible.value = false;
                            }}
                            className="flex justify-between gap-x-2 border-b border-gray-200 fill-current px-2 py-0.5 last:border-b-0 hover:text-sky-500 focus:bg-sky-100"
                        >
                            <span>{tag.name}</span>
                            {tag.t === "tag" ? (
                                <LabelIcon width="16" height="16" />
                            ) : (
                                <OpenInNewIcon width="16" height="16" />
                            )}
                        </button>
                    ))}
                </div>
            ) : (
                <p className="p-4 text-gray-500">No such tags or pages found</p>
            )}
        </div>
    );
};

export default Suggestions;
